// src/pages/usage/BixMailSeatsPanel.jsx
//
// BixMail mailbox-seat add-on panel — admin panel edition.
// Adapted from src/pages/Plans/billing/BixMailSeatsPanel.jsx (main Bixjet app).
//
// • Fetches zapmailEntitlement from zapmail-service (REACT_APP_ZAPMAIL_API).
//   zapmailEntitlement lives only on zapmail-service's own Apollo server,
//   NOT in the main admin GraphQL schema.
// • Quantity stepper + "Add seats" → addBixmailSeats mutation on zapmail-service.
//
// Props
//   businessId      — string  (required)
//   defaultExpanded — bool
//   onChanged       — fn()    (optional; called after seats are added)
//
// Env vars (add to .env):
//   REACT_APP_ZAPMAIL_API=https://mail-api.bixjet.com/graphql

import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../store/AuthContext";
import {
    Box, Typography, Button, CircularProgress, Alert, Grid, Divider,
    TextField, Chip, Stack, Dialog, DialogTitle, DialogContent,
    DialogContentText, DialogActions, Collapse, IconButton, Snackbar,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import KeyboardArrowDownIcon    from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon      from "@mui/icons-material/KeyboardArrowUp";
import MailOutlineIcon          from "@mui/icons-material/MailOutline";
import AddCircleOutlineIcon     from "@mui/icons-material/AddCircleOutline";
import CheckCircleOutlineIcon   from "@mui/icons-material/CheckCircleOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";

// ── API endpoint ──────────────────────────────────────────────────────────────

const ZAPMAIL_API_URL =
    process.env.REACT_APP_ZAPMAIL_API ||
    "https://mail-api.bixjet.com/graphql";

// ── GraphQL fetch ─────────────────────────────────────────────────────────────

async function gqlFetch(url, query, variables, token) {
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(url, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors?.length) throw new Error(json.errors[0].message);
    return json.data;
}

// ── GraphQL documents ─────────────────────────────────────────────────────────

const ZAPMAIL_ENTITLEMENT_QUERY = `
  query ZapmailEntitlement($businessid: ID!) {
    zapmailEntitlement(businessid: $businessid) {
      seats
      paidseats
      manualseats
      status
      graceuntil
      cancelat
      manualexpiry
      subscriptionitemid
      lastpaymentat
      mailboxused
      mailboxsuspended
      mailboxremaining
      domainlimit
      domainused
      canAddDomain
      canAddMailbox
      message
      buyurl
    }
  }
`;

const ADD_BIXMAIL_SEATS_MUTATION = `
  mutation AddBixmailSeats($businessid: ID!, $count: Int!) {
    addBixmailSeats(businessid: $businessid, count: $count) {
      seats
      paidseats
      manualseats
      status
      mailboxused
      mailboxremaining
      mailboxsuspended
      canAddDomain
      canAddMailbox
      message
      buyurl
    }
  }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────

const PRICE_PER_SEAT = 5; // $5 / seat / month

const statusBadge = (status) => {
    const s = String(status || "none").toLowerCase();
    if (s === "active")   return { label: "Active",      icon: <CheckCircleOutlineIcon   sx={{ fontSize: 13 }} />, tone: "success" };
    if (s === "past_due") return { label: "Payment due", icon: <WarningAmberOutlinedIcon sx={{ fontSize: 13 }} />, tone: "warning" };
    if (s === "suspended" || s === "cancelled" || s === "canceled")
        return {
            label: s === "suspended" ? "Suspended" : "Cancelled",
            icon: <HighlightOffOutlinedIcon sx={{ fontSize: 13 }} />,
            tone: "error",
        };
    return { label: "No plan", icon: null, tone: "info" };
};

async function callAddSeats({ businessid, count, token }) {
    const data = await gqlFetch(
        ZAPMAIL_API_URL,
        ADD_BIXMAIL_SEATS_MUTATION,
        { businessid, count },
        token,
    );
    const ent = data?.addBixmailSeats;
    if (!ent) throw new Error("No entitlement returned");
    return ent;
}

// ── BixMailSeatsPanel ─────────────────────────────────────────────────────────

export default function BixMailSeatsPanel({ businessId, defaultExpanded = false, onChanged }) {
    const { token: authToken } = useAuth();

    const [expanded,    setExpanded]   = useState(defaultExpanded);
    const [ent,         setEnt]        = useState(null);
    const [entError,    setEntError]   = useState(null);
    const [loading,     setLoading]    = useState(false);
    const [busy,        setBusy]       = useState(false);
    const [qty,         setQty]        = useState(1);
    const [qtyError,    setQtyError]   = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);

    // Inline toast state (replaces ToastifyNotification from main app)
    const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
    const showToast = (message, severity = "success") =>
        setToast({ open: true, message, severity });

    useEffect(() => { if (defaultExpanded) setExpanded(true); }, [defaultExpanded]);

    const fetchEntitlement = useCallback(() => {
        if (!businessId) return;
        setLoading(true);
        setEntError(null);
        gqlFetch(ZAPMAIL_API_URL, ZAPMAIL_ENTITLEMENT_QUERY, { businessid: businessId }, authToken)
            .then((data) => setEnt(data?.zapmailEntitlement || null))
            .catch((err) => setEntError(err?.message || "Failed to load BixMail plan"))
            .finally(() => setLoading(false));
    }, [businessId, authToken]);

    useEffect(() => {
        if (expanded && !loading && ent === null && entError === null) fetchEntitlement();
    }, [expanded, ent, entError, loading, fetchEntitlement]);

    const totalPrice = (qty * PRICE_PER_SEAT).toFixed(2);

    const handleBuy = async () => {
        setConfirmOpen(false);
        setBusy(true);
        try {
            const updated = await callAddSeats({ businessid: businessId, count: qty, token: authToken });
            setEnt(updated);
            setQty(1);
            showToast(`${qty} seat${qty !== 1 ? "s" : ""} added successfully`, "success");
            onChanged?.();
        } catch (err) {
            showToast(err?.message || "Could not add seats", "error");
        } finally {
            setBusy(false);
        }
    };

    const badge = statusBadge(ent?.status);
    const hasSeats = (ent?.seats ?? 0) > 0;

    return (
        <>
            <Box sx={{
                border: "1px solid", borderColor: "divider",
                borderRadius: 1.5, bgcolor: "background.paper", overflow: "hidden",
            }}>
                {/* ── Header row ── */}
                <Box
                    role="button" tabIndex={0} aria-expanded={expanded}
                    onClick={() => setExpanded((p) => !p)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault(); setExpanded((p) => !p);
                        }
                    }}
                    sx={{
                        display: "flex", alignItems: "center", gap: 1.5,
                        p: 2, cursor: "pointer",
                        transition: "background-color 0.15s ease",
                        "&:hover":         { bgcolor: (t) => alpha(t.palette.text.primary, 0.02) },
                        "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 },
                    }}
                >
                    <Box sx={{
                        width: 32, height: 32, borderRadius: 0.75,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                        color: "primary.main", flexShrink: 0,
                    }}>
                        <MailOutlineIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>BixMail Seats</Typography>
                        {ent && !expanded && (
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                {ent.seats} seat{ent.seats !== 1 ? "s" : ""} · {ent.mailboxused ?? 0} used
                            </Typography>
                        )}
                    </Box>
                    {ent && !expanded && (
                        <Chip
                            size="small"
                            icon={badge.icon || undefined}
                            label={badge.label}
                            sx={{
                                height: 22, fontSize: 11, fontWeight: 700,
                                bgcolor: (t) => alpha(t.palette[badge.tone]?.main || t.palette.text.primary, 0.12),
                                color: `${badge.tone}.main`,
                                "& .MuiChip-icon": { color: `${badge.tone}.main`, ml: 0.5 },
                            }}
                        />
                    )}
                    <IconButton size="small" tabIndex={-1}
                        sx={{ color: "text.disabled", "&:hover": { bgcolor: "transparent", color: "text.primary" } }}>
                        {expanded
                            ? <KeyboardArrowUpIcon sx={{ fontSize: 20 }} />
                            : <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />}
                    </IconButton>
                </Box>

                {/* ── Body ── */}
                <Collapse in={expanded} timeout={250}>
                    <Box sx={{ borderTop: "1px solid", borderColor: "divider", p: { xs: 2, md: 3 } }}>

                        {loading && (
                            <Box sx={{ py: 3, textAlign: "center" }}>
                                <CircularProgress size={22} />
                            </Box>
                        )}

                        {!loading && entError && (
                            <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5, fontSize: 12.5 }}
                                action={
                                    <Button color="inherit" size="small"
                                        onClick={() => { setEntError(null); fetchEntitlement(); }}>
                                        Retry
                                    </Button>
                                }>
                                {entError}
                            </Alert>
                        )}

                        {!loading && ent && (
                            <>
                                {ent.status === "past_due" && (
                                    <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 1.5, fontSize: 12.5 }}>
                                        BixMail payment is past due.
                                        {ent.graceuntil && ` Grace period ends ${new Date(ent.graceuntil).toLocaleDateString()}.`}
                                        {" "}Adding seats will clear the balance.
                                    </Alert>
                                )}
                                {ent.status === "suspended" && (
                                    <Alert severity="error" sx={{ mb: 2.5, borderRadius: 1.5, fontSize: 12.5 }}>
                                        BixMail is suspended. Mailboxes are paused. Add seats to restore access.
                                    </Alert>
                                )}
                                {ent.message && ent.status !== "past_due" && ent.status !== "suspended" && (
                                    <Alert severity="info" sx={{ mb: 2.5, borderRadius: 1.5, fontSize: 12.5 }}>
                                        {ent.message}
                                    </Alert>
                                )}

                                <Grid container spacing={3}>
                                    {/* Left — qty stepper */}
                                    <Grid item xs={12} md={6}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                                            Buy Mailbox Seats
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>
                                            Each seat lets you add one active sending mailbox.
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                            <TextField
                                                label="Seats to buy" size="small" type="number" fullWidth
                                                value={qty} error={Boolean(qtyError)}
                                                helperText={qtyError || " "}
                                                inputProps={{ min: 1, max: 50 }}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    if (!raw.match(/^[0-9]*$/)) { setQtyError("Enter a valid number"); return; }
                                                    setQtyError("");
                                                    const clamped = Math.max(1, Math.min(50, parseInt(raw || "1", 10)));
                                                    setQty(clamped);
                                                }}
                                                sx={{ maxWidth: 200 }}
                                            />
                                            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                                                Estimated total:&nbsp;
                                                <Box component="span" sx={{ fontWeight: 800, color: "text.primary" }}>
                                                    ${totalPrice}
                                                </Box>
                                                &nbsp;/ month
                                                <Typography component="span" sx={{ fontSize: 11, color: "text.disabled", ml: 0.5 }}>
                                                    (${PRICE_PER_SEAT}/seat)
                                                </Typography>
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* Right — usage stats */}
                                    <Grid item xs={12} md={6}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                                            Current Usage
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>
                                            Active mailbox allocation.
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                        <Stack spacing={1.5}>
                                            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                                                <Typography sx={{ fontSize: 32, fontWeight: 800, lineHeight: 1, color: "text.primary" }}>
                                                    {ent.seats ?? 0}
                                                </Typography>
                                                <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                                                    total seat{(ent.seats ?? 0) !== 1 ? "s" : ""}
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                                                <Box>
                                                    <Typography sx={{ fontSize: 10.5, color: "text.disabled", fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", mb: 0.25 }}>Used</Typography>
                                                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{ent.mailboxused ?? 0}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontSize: 10.5, color: "text.disabled", fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", mb: 0.25 }}>Remaining</Typography>
                                                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: (ent.mailboxremaining ?? 0) > 0 ? "success.main" : "error.main" }}>
                                                        {ent.mailboxremaining ?? 0}
                                                    </Typography>
                                                </Box>
                                                {(ent.mailboxsuspended ?? 0) > 0 && (
                                                    <Box>
                                                        <Typography sx={{ fontSize: 10.5, color: "text.disabled", fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", mb: 0.25 }}>Suspended</Typography>
                                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "warning.main" }}>{ent.mailboxsuspended}</Typography>
                                                    </Box>
                                                )}
                                                <Box>
                                                    <Typography sx={{ fontSize: 10.5, color: "text.disabled", fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", mb: 0.25 }}>Status</Typography>
                                                    <Chip
                                                        size="small"
                                                        icon={badge.icon || undefined}
                                                        label={badge.label}
                                                        sx={{
                                                            height: 20, fontSize: 11, fontWeight: 700,
                                                            bgcolor: (t) => alpha(t.palette[badge.tone]?.main || t.palette.text.primary, 0.12),
                                                            color: `${badge.tone}.main`,
                                                            "& .MuiChip-icon": { color: `${badge.tone}.main`, ml: 0.5 },
                                                        }}
                                                    />
                                                </Box>
                                            </Stack>
                                        </Stack>
                                    </Grid>

                                    {/* Action row */}
                                    <Grid item xs={12}>
                                        <Divider sx={{ mb: 2 }} />
                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                                            <Button
                                                variant="contained" color="primary" size="small"
                                                disabled={busy || Boolean(qtyError)}
                                                startIcon={busy
                                                    ? <CircularProgress size={14} color="inherit" />
                                                    : <AddCircleOutlineIcon sx={{ fontSize: 16 }} />}
                                                onClick={() => setConfirmOpen(true)}
                                                sx={{
                                                    textTransform: "none", fontWeight: 700, fontSize: 13,
                                                    borderRadius: 1.5, boxShadow: "none",
                                                    "&:hover": { boxShadow: "none" },
                                                }}
                                            >
                                                {busy ? "Adding seats…" : (hasSeats ? "Add more seats" : "Add seats")}
                                            </Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </Box>
                </Collapse>
            </Box>

            {/* ── Confirm dialog ── */}
            <Dialog
                open={confirmOpen}
                onClose={() => !busy && setConfirmOpen(false)}
                disableEscapeKeyDown={busy}
                maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: 2.5 } }}
            >
                <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>
                    Buy {qty} BixMail Seat{qty !== 1 ? "s" : ""}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: 13.5 }}>
                        Add <strong>{qty} mailbox seat{qty !== 1 ? "s" : ""}</strong> to this business's BixMail plan?
                        The seat limit will increase immediately.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        variant="outlined" onClick={() => setConfirmOpen(false)} disabled={busy}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained" color="primary" onClick={handleBuy} disabled={busy}
                        startIcon={busy
                            ? <CircularProgress size={14} color="inherit" />
                            : <AddCircleOutlineIcon sx={{ fontSize: 14 }} />}
                        sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, boxShadow: "none" }}
                    >
                        {busy ? "Adding…" : "Confirm"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Toast ── */}
            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast((p) => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={toast.severity}
                    onClose={() => setToast((p) => ({ ...p, open: false }))}
                    sx={{ borderRadius: 1.5, fontSize: 13 }}
                >
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
}